/**
 * A part of a word was capitalised on purpose when it has two or more capitals
 * ("NPL", "CASA", "CEOs", "eKYC"), or one capital with no lower case beside it:
 * the "I" of "Tier I", the "Q4" of a quarter.
 */
const hasCapitalsOnPurpose = (part: string): boolean => {
  const capitals = part.match(/[A-Z]/g)?.length ?? 0
  return capitals >= 2 || (capitals === 1 && !/[a-z]/.test(part))
}

/** The parts of a hyphenated or slashed word ("Non-Performing", "P/E"). */
const PARTS = /[^-/]+/g

const upperFirst = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)

/**
 * Sentence case: the first word capitalised, every other word lower case, and
 * whatever was capitalised on purpose left alone.
 *
 * The data service writes its labels in Title Case ("Return on Equity") and the
 * app sets every title in sentence case, so the rule has to read what a capital
 * MEANS (`hasCapitalsOnPurpose`). Hyphens and slashes split a word into parts
 * that are judged apart: "Non-Performing" becomes "Non-performing", while
 * "NPL/Loans" keeps its NPL.
 *
 * SHOUTED text ("RIGHT SHARE") has no such signal: every letter is a capital,
 * so nothing tells AGM from RIGHT. Pass `acronyms` for it, and only those words
 * keep their capitals.
 */
export const sentenceCase = (text: string, acronyms?: ReadonlySet<string>): string => {
  const keeps = (part: string): boolean =>
    acronyms === undefined ? hasCapitalsOnPurpose(part) : acronyms.has(part)

  return text
    .trim()
    .split(/\s+/)
    .map((word, index) =>
      word.replace(PARTS, (part, offset: number) => {
        if (keeps(part)) return part
        const lower = part.toLowerCase()
        return index === 0 && offset === 0 ? upperFirst(lower) : lower
      }),
    )
    .join(' ')
}
